
import { useState } from "react";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useContractForm } from "@/hooks/useContractForm";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";
import { TranslationButton } from "@/components/i18n/TranslationButton";
import { v4 as uuidv4 } from "uuid";

// Required sections
import { RequiredContractDetailsSection } from "./sections/RequiredContractDetailsSection";
import { RequiredFinancialDetailsSection } from "./sections/RequiredFinancialDetailsSection";
import { PaymentTypeSection } from "../form/sections/PaymentTypeSection";

// Optional sections
import { ContractReminders } from "../form/ContractReminders";
import { ContractAttachments } from "../form/ContractAttachments";
import { ContractContactsSection } from "../form/sections/ContractContactsSection";
import { FeatureGuard } from "@/components/common/FeatureGuard";
import { ContractServicesSection } from "../form/sections/ContractServicesSection";
import { QuickAddFormModals } from "./QuickAddFormModals";

interface QuickAddContractFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function QuickAddContractForm({ onSuccess, onCancel }: QuickAddContractFormProps) {
  const { t } = useEnhancedTranslation();
  const [isOptionalSectionOpen, setIsOptionalSectionOpen] = useState(false);

  // Generate a temporary ID for the new contract
  const tempContractId = uuidv4();
  const initialContract = { id: tempContractId };
  
  const {
    form,
    selectedContractType,
    setSelectedContractType,
    selectedContacts,
    setSelectedContacts,
    attachments,
    setAttachments,
    existingAttachments,
    setExistingAttachments,
    contractTypeModalOpen,
    setContractTypeModalOpen,
    categoryModalOpen,
    setCategoryModalOpen,
    contactModalOpen,
    setContactModalOpen,
    currencyModalOpen,
    setCurrencyModalOpen,
    renewalDate,
    servicesTotal,
    handleContractTypeCreated,
    handleCategoryCreated,
    handleContactCreated,
    handleCurrencyCreated,
    handleCompanySelectWithFormUpdate,
    handleServicesTotalUpdate,
    handleServicesApplyCallback,
    onSubmit,
    handleCancelWithConfirmation,
    isPending,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    confirmNavigation,
    cancelNavigationDialog,
  } = useContractForm(initialContract, "create");

  const contractTypeId = form.watch("contractTypeId");
  const currencyId = form.watch("currencyId");
  const showServicesSection = contractTypeId && currencyId;
  const showServicesSectionMessage = !showServicesSection;
  
  const handleSubmit = async (values: any) => {
    await onSubmit(values);
    onSuccess();
  };

  return (
    <>
      <QuickAddFormModals
        contractTypeModalOpen={contractTypeModalOpen}
        setContractTypeModalOpen={setContractTypeModalOpen}
        categoryModalOpen={categoryModalOpen}
        setCategoryModalOpen={setCategoryModalOpen}
        contactModalOpen={contactModalOpen}
        setContactModalOpen={setContactModalOpen}
        currencyModalOpen={currencyModalOpen}
        setCurrencyModalOpen={setCurrencyModalOpen}
        showUnsavedChangesDialog={showUnsavedChangesDialog}
        setShowUnsavedChangesDialog={setShowUnsavedChangesDialog}
        onConfirmNavigation={confirmNavigation}
        onCancelNavigation={cancelNavigationDialog}
        onContractTypeCreated={handleContractTypeCreated}
        onCategoryCreated={handleCategoryCreated}
        onContactCreated={handleContactCreated}
        onCurrencyCreated={handleCurrencyCreated}
      />
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="space-y-6">
            {/* Required sections - always visible */}
            <div className="grid gap-6 md:grid-cols-2">
              <RequiredContractDetailsSection 
                form={form}
                selectedContractType={selectedContractType}
                setSelectedContractType={setSelectedContractType}
                setContractTypeModalOpen={setContractTypeModalOpen}
                setCategoryModalOpen={setCategoryModalOpen}
                handleCompanySelect={handleCompanySelectWithFormUpdate}
              />
              
              <div className="space-y-6">
                <RequiredFinancialDetailsSection 
                  form={form}
                  setCurrencyModalOpen={setCurrencyModalOpen}
                  servicesTotal={servicesTotal}
                  selectedContractType={selectedContractType}
                />
                
                <FeatureGuard feature="payment_type_selection">
                  <PaymentTypeSection form={form} />
                </FeatureGuard>
              </div>
            </div>
            
            <Separator className="my-4" />
            
            {/* Optional sections - collapsible */}
            <Collapsible 
              open={isOptionalSectionOpen} 
              onOpenChange={setIsOptionalSectionOpen}
              className="border rounded-md"
            >
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="flex justify-between w-full p-4 h-auto">
                  <span className="font-medium">{t('optionalDetails', 'Optional Details')}</span>
                  {isOptionalSectionOpen ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </Button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="p-4 pt-0 space-y-6">
                <FeatureGuard feature="contract_services">
                  {showServicesSection ? (
                    <ContractServicesSection
                      form={form}
                      contractId={initialContract.id}
                      contractTypeId={contractTypeId}
                      currencyId={currencyId}
                      onTotalCostChange={handleServicesTotalUpdate}
                      isTemporary={true}
                      onApplyCallback={handleServicesApplyCallback}
                    />
                  ) : showServicesSectionMessage && (
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium">{t('services', 'Services')}</h3>
                      <p className="text-sm text-muted-foreground">
                        {t('selectContractTypeAndCurrencyToAddServices', 'Please select a contract type and currency to add services')}
                      </p>
                    </div>
                  )}
                </FeatureGuard>
                
                {showServicesSection && <Separator className="my-4" />}
                
                <FeatureGuard feature="reminders">
                  <ContractReminders form={form} />
                  <Separator className="my-4" />
                </FeatureGuard>
                
                <ContractContactsSection 
                  selectedContacts={selectedContacts}
                  setSelectedContacts={setSelectedContacts}
                  setContactModalOpen={setContactModalOpen}
                  existingContacts={[]}
                />

                <Separator className="my-4" />
                
                <ContractAttachments 
                  attachments={attachments}
                  setAttachments={setAttachments}
                  existingAttachments={existingAttachments}
                  setExistingAttachments={setExistingAttachments}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <TranslationButton 
              type="button" 
              variant="outline" 
              onClick={handleCancelWithConfirmation}
              i18nKey="cancel"
            >
              {t("cancel", "Cancel")}
            </TranslationButton>
            <TranslationButton 
              type="submit"
              disabled={isPending}
              i18nKey="createContract"
            >
              {isPending ? t("creating", "Creating...") : t("createContract", "Create Contract")}
            </TranslationButton>
          </div>
        </form>
      </Form>
    </>
  );
}
